import { useEffect, useState, useCallback } from "react";
import { ref, onValue, set, remove, update, serverTimestamp } from "firebase/database";
import { v4 as uuidv4 } from "uuid";
import { rtdb } from "@/lib/firebase";
import { pruneUndefined } from "@/lib/rtdbSafe";
import type { DraftCartLine, SelectedRubric } from "@/lib/types";
import type { ParsedQr } from "@/lib/qrParse";

interface AddLineInput {
  productId: string;
  qty?: number;
  unitPrice: number;
  options?: SelectedRubric[];
  productSnapshot?: DraftCartLine["productSnapshot"];
}

interface UseSharedCartResult {
  lines: Record<string, DraftCartLine>;
  addLine: (input: AddLineInput) => Promise<string>;
  /**
   * `byName` (optional) tags the line with the deleter's display name immediately
   * before removal, so the owner's notifier can show "removed by {name}" instead
   * of the generic fallback.
   */
  removeLine: (lineId: string, byName?: string) => Promise<void>;
  updateQty: (lineId: string, qty: number) => Promise<void>;
  isFrozen: boolean;
}

export const useSharedCart = (
  parsed: ParsedQr | null,
  participantId: string,
  isFrozen: boolean
): UseSharedCartResult => {
  const [lines, setLines] = useState<Record<string, DraftCartLine>>({});

  useEffect(() => {
    if (!parsed) return;
    const draftRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/draftCart`);
    const unsub = onValue(draftRef, (snap) => {
      setLines((snap.val() as Record<string, DraftCartLine>) || {});
    });
    return () => unsub();
  }, [parsed?.restoId, parsed?.qrId]);

  const addLine = useCallback(
    async (input: AddLineInput): Promise<string> => {
      if (!parsed) throw new Error("no session");
      if (isFrozen) throw new Error("frozen");
      const lineId = uuidv4();
      const lineRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/draftCart/${lineId}`);
      const payload: Record<string, unknown> = {
        productId: input.productId,
        qty: input.qty ?? 1,
        unitPrice: input.unitPrice,
        addedBy: participantId,
        addedAt: serverTimestamp() as unknown as number,
      };
      if (input.options && input.options.length) payload.options = input.options;
      if (input.productSnapshot) payload.productSnapshot = input.productSnapshot;
      try {
        await set(lineRef, pruneUndefined(payload));
        return lineId;
      } catch (err) {
        console.error("[useSharedCart.addLine] write failed", { lineId, err });
        throw err;
      }
    },
    [parsed?.restoId, parsed?.qrId, participantId, isFrozen]
  );

  const removeLine = useCallback(
    async (lineId: string, byName?: string): Promise<void> => {
      if (!parsed) throw new Error("no session");
      if (isFrozen) throw new Error("frozen");
      const lineRef = ref(rtdb, `${parsed.restoId}/${parsed.qrId}/draftCart/${lineId}`);
      try {
        // Tag the line with the deleter's nickname before deletion so other
        // clients' `useRemovalNotifier` can read `prev[lineId].removedByName`
        // when the next snapshot drops the line entirely.
        if (byName) await update(lineRef, { removedByName: byName });
        await remove(lineRef);
      } catch (err) {
        console.error("[useSharedCart.removeLine] write failed", { lineId, err });
        throw err;
      }
    },
    [parsed?.restoId, parsed?.qrId, isFrozen]
  );

  const updateQty = useCallback(
    async (lineId: string, qty: number): Promise<void> => {
      if (!parsed) throw new Error("no session");
      if (isFrozen) throw new Error("frozen");
      try {
        if (qty <= 0) {
          await remove(ref(rtdb, `${parsed.restoId}/${parsed.qrId}/draftCart/${lineId}`));
          return;
        }
        await update(
          ref(rtdb, `${parsed.restoId}/${parsed.qrId}/draftCart/${lineId}`),
          pruneUndefined({ qty })
        );
      } catch (err) {
        console.error("[useSharedCart.updateQty] write failed", { lineId, qty, err });
        throw err;
      }
    },
    [parsed?.restoId, parsed?.qrId, isFrozen]
  );

  return { lines, addLine, removeLine, updateQty, isFrozen };
};
